import { useRef, type KeyboardEvent } from 'react'
import { cn } from '@/lib/utils'
import { CHANGED_PRESETS, formatRangeSpoken, pastRange } from '../query/dateRanges'
import type { Filter } from '../query/types'
import { changedChoice, quickFor, setChanged, type ChangedPreset } from './quickConditions'

interface Chip {
  /** The preset it sets; null is "Alles". */
  key: ChangedPreset | null
  text: string
  /** What a screen reader says: the preset with the days it covers. */
  name: string
}

const capitalise = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)
const ARROWS: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }

/**
 * "Gewijzigd" on a phone (addendum A.5.4, ED-60): a row of 44 px chips above the card list, one
 * radio group that scrolls sideways. A range in the accessible name: "Afgelopen 2 weken, 3 tot en
 * met 16 oktober". "Alles" takes the condition off. It sets only the period; who did it is in the
 * filter sheet.
 */
export function ChangedChipRow({ filter, today, onChange }: { filter: Filter; today: string; onChange: (next: Filter) => void }) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([])
  const choice = changedChoice(filter)
  const chips: Chip[] = [
    { key: null, text: 'Alles', name: 'Alles' },
    ...CHANGED_PRESETS.map((preset) => {
      const range = pastRange({ property: 'last_activity_at', ...preset.condition }, today)
      return {
        key: preset.key as ChangedPreset,
        text: capitalise(preset.short),
        name: range && 'from' in range ? `${preset.label}, ${formatRangeSpoken(range)}` : preset.label,
      }
    }),
  ]
  // A range of your own, or a condition that is no preset, checks none of the chips.
  const allOn = quickFor(filter, 'last_activity_at').length === 0
  const checkedAt = chips.findIndex((chip) => (chip.key === null ? allOn : chip.key === choice.preset))

  function select(chip: Chip) {
    onChange(setChanged(filter, chip.key === null ? { by: null } : { preset: chip.key, by: choice.by }))
  }

  // The radio pattern: the arrow keys move the choice, Tab leaves the group.
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const step = ARROWS[event.key]
    if (!step) return
    event.preventDefault()
    const focused = buttons.current.findIndex((button) => button === document.activeElement)
    const from = focused >= 0 ? focused : Math.max(checkedAt, 0)
    const next = (from + step + chips.length) % chips.length
    select(chips[next])
    buttons.current[next]?.focus()
  }

  return (
    <div className="flex items-center gap-2 overflow-x-auto md:hidden">
      <span aria-hidden="true" className="shrink-0 text-sm text-muted-foreground">
        Gewijzigd:
      </span>
      <div role="radiogroup" aria-label="Gewijzigd" onKeyDown={onKeyDown} className="flex gap-2">
        {chips.map((chip, index) => {
          const checked = index === checkedAt
          return (
            <button
              key={chip.name}
              ref={(node) => {
                buttons.current[index] = node
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              aria-label={chip.name}
              tabIndex={checked || (checkedAt < 0 && index === 0) ? 0 : -1}
              onClick={() => select(chip)}
              className={cn(
                'h-11 shrink-0 rounded-full border px-4 text-sm',
                checked ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground',
              )}
            >
              {chip.text}
            </button>
          )
        })}
      </div>
    </div>
  )
}
